import { Info as InfoIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment, { type Moment } from "moment";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import Swal from "sweetalert2";
import {
  cancelNotification,
  getNotificationHistory,
} from "../../actions/notifications";
import { getUser } from "../../apis/user/user-apis";
import { DASHBOARD_USER } from "../../constants/routes";
import type { User } from "../../domain/user";

interface Notification {
  id: string;
  title: string;
  body: string;
  type: string;
  target_type: string;
  target_value: string | null;
  target_user_ids: string[] | null;
  target_city_ids: number[] | null;
  status: string;
  sent_count: number;
  failed_count: number;
  fcm_message_id: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: legacy V1 type
  delivery_errors: any[] | null;
  sent_by_user_email: string | null;
  sent_by_user_id: string | null;
  creation_datetime: string;
  sent_at: string | null;
  // biome-ignore lint/suspicious/noExplicitAny: legacy V1 type
  data: Record<string, any>;
  fan_out_status: string | null;
  fan_out_started_at: string | null;
  fan_out_completed_at: string | null;
  fan_out_error: string | null;
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  sent: "success",
  pending: "warning",
  failed: "error",
  scheduled: "info",
  cancelled: "default",
};

const fanOutStatusColors: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  PENDING: "warning",
  IN_PROGRESS: "info",
  COMPLETED: "success",
  FAILED: "error",
};

const getInboxDeliveryLabel = (notif: Notification): string => {
  if (!notif.fan_out_status) {
    if (notif.target_type === "users") {
      return notif.sent_count > 0 ? `${notif.sent_count} users` : "-";
    }
    return "-";
  }

  switch (notif.fan_out_status) {
    case "PENDING":
      return "Preparing...";
    case "IN_PROGRESS":
      return "Delivering...";
    case "COMPLETED":
      return `${notif.sent_count.toLocaleString()} users`;
    case "FAILED":
      return "Failed";
    default:
      return "-";
  }
};

const getInboxDeliveryTooltip = (notif: Notification): string => {
  if (!notif.fan_out_status) return "";

  switch (notif.fan_out_status) {
    case "PENDING":
      return "Notification is queued for inbox delivery";
    case "IN_PROGRESS":
      return "Currently delivering to user inboxes";
    case "COMPLETED":
      return `Successfully delivered to ${notif.sent_count.toLocaleString()} user inboxes`;
    case "FAILED":
      return notif.fan_out_error || "Inbox delivery failed";
    default:
      return "";
  }
};

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return "-";
  return moment(dateString).format("MMM D, YYYY HH:mm");
};

export const NotificationsHistory = () => {
  const dispatch = useDispatch();
  const {
    history,
    historyLoading,
    historyError,
    historyTotalCount,
    cancelling,
    cancelSuccess,
    cancelError,
    // biome-ignore lint/suspicious/noExplicitAny: legacy V1 untyped Redux store
  } = useSelector((state: any) => state.notifications);

  // biome-ignore lint/suspicious/noExplicitAny: legacy V1 untyped Redux store
  const { citiesList = [] } = useSelector((state: any) => state.cities);

  const citiesMap = useMemo(() => {
    // biome-ignore lint/suspicious/noExplicitAny: legacy V1 untyped city
    return citiesList.reduce((acc: Record<number, string>, city: any) => {
      acc[city.id] = city.name;
      return acc;
    }, {});
  }, [citiesList]);

  const resolveCityName = (cityId: string) => {
    return citiesMap[Number(cityId)] || `City #${cityId}`;
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Moment | null>(
    moment().subtract(30, "days")
  );
  const [dateTo, setDateTo] = useState<Moment | null>(moment().add(1, "days"));
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [usersMap, setUsersMap] = useState<Map<string, User>>(new Map());

  const allUserIds = useMemo(() => {
    const ids = new Set<string>();
    for (const notif of history) {
      if (notif.target_user_ids) {
        for (const id of notif.target_user_ids) {
          ids.add(id);
        }
      }
    }
    return Array.from(ids);
  }, [history]);

  useEffect(() => {
    const fetchUsers = async () => {
      const newUsersMap = new Map(usersMap);
      const idsToFetch = allUserIds.filter((id) => !newUsersMap.has(id));

      for (const id of idsToFetch.slice(0, 50)) {
        try {
          const user = await getUser(parseInt(id, 10));
          newUsersMap.set(id, user);
        } catch {
          // User not found, skip
        }
      }

      if (idsToFetch.length > 0) {
        setUsersMap(newUsersMap);
      }
    };

    if (allUserIds.length > 0) {
      fetchUsers();
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omitting usersMap to avoid infinite loop
  }, [allUserIds]);

  const getUserDisplay = (userId: string) => {
    const user = usersMap.get(userId);
    if (user) {
      return user.fullName || user.email || user.phone || `User ${userId}`;
    }
    return `User ${userId}`;
  };

  const fetchHistory = () => {
    dispatch(
      getNotificationHistory({
        status: statusFilter !== "all" ? statusFilter : undefined,
        created_after: dateFrom?.toISOString(),
        created_before: dateTo?.toISOString(),
      })
    );
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchHistory is not memoized in V1
  useEffect(() => {
    fetchHistory();
  }, [statusFilter, dateFrom, dateTo]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedHistory = history.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getTargetLabel = (notif: Notification) => {
    if (notif.target_type === "users" && notif.target_user_ids) {
      const count = notif.target_user_ids.length;
      if (count === 1) {
        return getUserDisplay(notif.target_user_ids[0]);
      }
      return `${count} users`;
    }
    // New backend target_type: "cities" with target_city_ids array
    if (
      notif.target_type === "cities" &&
      notif.target_city_ids &&
      notif.target_city_ids.length > 0
    ) {
      const firstName = resolveCityName(String(notif.target_city_ids[0]));
      if (notif.target_city_ids.length === 1) return firstName;
      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{firstName}</span>
          <Chip
            label={`+${notif.target_city_ids.length - 1}`}
            size="small"
            sx={{ height: 20, fontSize: 11 }}
          />
        </Stack>
      );
    }
    if (notif.target_value) {
      if (notif.target_value.startsWith("whitelabel_id-")) {
        return `App #${notif.target_value.replace("whitelabel_id-", "")}`;
      }
      if (notif.target_value.startsWith("city_id-")) {
        const cityId = notif.target_value.replace("city_id-", "");
        return resolveCityName(cityId);
      }
      // Legacy: multi-city condition from old per-city sends
      if (notif.target_type === "condition") {
        const cityIds = notif.target_value.match(/city_id-(\d+)/g);
        if (cityIds && cityIds.length > 0) {
          const firstName = resolveCityName(cityIds[0].replace("city_id-", ""));
          if (cityIds.length === 1) return firstName;
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <span>{firstName}</span>
              <Chip
                label={`+${cityIds.length - 1}`}
                size="small"
                sx={{ height: 20, fontSize: 11 }}
              />
            </Stack>
          );
        }
      }
      return notif.target_value;
    }
    return notif.target_type;
  };

  const getTargetTooltip = (notif: Notification) => {
    if (
      notif.target_type === "users" &&
      notif.target_user_ids &&
      notif.target_user_ids.length > 1
    ) {
      return notif.target_user_ids.map((id) => getUserDisplay(id)).join(", ");
    }
    // New backend target_type: "cities"
    if (
      notif.target_type === "cities" &&
      notif.target_city_ids &&
      notif.target_city_ids.length > 1
    ) {
      return notif.target_city_ids
        .map((id) => resolveCityName(String(id)))
        .join(", ");
    }
    // Legacy: condition-based multi-city
    if (notif.target_type === "condition" && notif.target_value) {
      const cityIds = notif.target_value.match(/city_id-(\d+)/g);
      if (cityIds && cityIds.length > 1) {
        return cityIds
          .map((m) => resolveCityName(m.replace("city_id-", "")))
          .join(", ");
      }
    }
    return "";
  };

  useEffect(() => {
    if (cancelSuccess) {
      if (cancelSuccess.success) {
        Swal.fire({
          title: "Cancelled",
          text: "The notification has been cancelled.",
          icon: "success",
          confirmButtonColor: "#4caf50",
        });
        setSelectedNotification(null);
        fetchHistory();
      } else {
        Swal.fire({
          title: "Failed",
          text: cancelSuccess.message || "Failed to cancel notification",
          icon: "error",
          confirmButtonColor: "#f44336",
        });
      }
    }
  }, [cancelSuccess]);

  useEffect(() => {
    if (cancelError) {
      Swal.fire({
        title: "Error",
        text: cancelError,
        icon: "error",
        confirmButtonColor: "#f44336",
      });
    }
  }, [cancelError]);

  const handleCancelNotification = async () => {
    if (!selectedNotification) return;

    const notificationToCancel = selectedNotification;
    setSelectedNotification(null);

    const result = await Swal.fire({
      title: "Cancel Scheduled Notification?",
      text: "This will prevent the notification from being sent. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "No, keep it",
      confirmButtonColor: "#f44336",
    });

    if (result.isConfirmed) {
      dispatch(cancelNotification(notificationToCancel.id));
    } else {
      setSelectedNotification(notificationToCancel);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography variant="h6">
          Notification History ({historyTotalCount})
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchHistory} disabled={historyLoading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <DatePicker
          label="From"
          value={dateFrom}
          onChange={setDateFrom}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />
        <DatePicker
          label="To"
          value={dateTo}
          onChange={setDateTo}
          slotProps={{ textField: { size: "small", sx: { width: 160 } } }}
        />
        <TextField
          select={true}
          label="Status"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="sent">Sent</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="scheduled">Scheduled</MenuItem>
          <MenuItem value="cancelled">Cancelled</MenuItem>
        </TextField>
      </Box>

      {historyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load history: {historyError}
        </Alert>
      )}

      {historyLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : history.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">No notifications found</Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Push Status</TableCell>
                  <TableCell>Inbox Delivery</TableCell>
                  <TableCell>Sent By</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedHistory.map((notif: Notification) => (
                  <TableRow
                    key={notif.id}
                    hover={true}
                    sx={{ cursor: "pointer" }}
                    onClick={() => setSelectedNotification(notif)}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(notif.creation_datetime)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        noWrap={true}
                        sx={{ maxWidth: 200 }}
                      >
                        {notif.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={getTargetTooltip(notif)}
                        placement="top"
                        arrow={true}
                      >
                        <Chip
                          label={getTargetLabel(notif)}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={notif.status}
                        size="small"
                        color={statusColors[notif.status] || "default"}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={getInboxDeliveryTooltip(notif)}
                        placement="top"
                        arrow={true}
                      >
                        <Chip
                          label={getInboxDeliveryLabel(notif)}
                          size="small"
                          color={
                            notif.fan_out_status
                              ? fanOutStatusColors[notif.fan_out_status] ||
                                "default"
                              : "default"
                          }
                          variant={notif.fan_out_status ? "filled" : "outlined"}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap={true}
                        sx={{ maxWidth: 150 }}
                      >
                        {notif.sent_by_user_email || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNotification(notif);
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={history.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </>
      )}

      <Dialog
        open={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        maxWidth="sm"
        fullWidth={true}
      >
        {selectedNotification && (
          <>
            <DialogTitle>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6">Notification Details</Typography>
                <Chip
                  label={selectedNotification.status}
                  color={statusColors[selectedNotification.status] || "default"}
                  size="small"
                />
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedNotification.title}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Body
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {selectedNotification.body}
                  </Typography>
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body2">
                      {selectedNotification.type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Target Type
                    </Typography>
                    <Typography variant="body2">
                      {selectedNotification.target_type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(selectedNotification.creation_datetime)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sent At
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(selectedNotification.sent_at)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Target Audience
                    </Typography>
                    <Typography variant="body2">
                      {selectedNotification.target_type === "users" &&
                      selectedNotification.target_user_ids
                        ? `${selectedNotification.target_user_ids.length} user(s)`
                        : "All subscribers"}
                    </Typography>
                  </Box>
                </Box>

                {selectedNotification.fan_out_status && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                        Inbox Delivery
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={
                                selectedNotification.fan_out_status ===
                                "PENDING"
                                  ? "Preparing"
                                  : selectedNotification.fan_out_status ===
                                      "IN_PROGRESS"
                                    ? "Delivering..."
                                    : selectedNotification.fan_out_status ===
                                        "COMPLETED"
                                      ? "Delivered"
                                      : selectedNotification.fan_out_status ===
                                          "FAILED"
                                        ? "Failed"
                                        : selectedNotification.fan_out_status
                              }
                              size="small"
                              color={
                                fanOutStatusColors[
                                  selectedNotification.fan_out_status
                                ] || "default"
                              }
                            />
                          </Box>
                        </Box>
                        {selectedNotification.fan_out_status ===
                          "COMPLETED" && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Users Reached
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedNotification.sent_count.toLocaleString()}{" "}
                              users
                            </Typography>
                          </Box>
                        )}
                        {selectedNotification.fan_out_started_at && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Started
                            </Typography>
                            <Typography variant="body2">
                              {formatDateTime(
                                selectedNotification.fan_out_started_at
                              )}
                            </Typography>
                          </Box>
                        )}
                        {selectedNotification.fan_out_completed_at && (
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Completed
                            </Typography>
                            <Typography variant="body2">
                              {formatDateTime(
                                selectedNotification.fan_out_completed_at
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      {selectedNotification.fan_out_status === "FAILED" &&
                        selectedNotification.fan_out_error && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              {selectedNotification.fan_out_error}
                            </Typography>
                          </Alert>
                        )}
                      {selectedNotification.fan_out_status ===
                        "IN_PROGRESS" && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            This notification is currently being delivered to
                            user inboxes. Refresh to see the latest status.
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </>
                )}

                <Divider />

                {(selectedNotification.target_value ||
                  selectedNotification.target_city_ids) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Target Value
                    </Typography>
                    <Typography variant="body2">
                      {selectedNotification.target_type === "cities" &&
                      selectedNotification.target_city_ids
                        ? selectedNotification.target_city_ids
                            .map((id) => resolveCityName(String(id)))
                            .join(", ")
                        : selectedNotification.target_type === "condition" &&
                            selectedNotification.target_value
                          ? (
                              selectedNotification.target_value.match(
                                /city_id-(\d+)/g
                              ) || []
                            )
                              .map((m) =>
                                resolveCityName(m.replace("city_id-", ""))
                              )
                              .join(", ") || selectedNotification.target_value
                          : selectedNotification.target_value?.startsWith(
                                "city_id-"
                              )
                            ? resolveCityName(
                                selectedNotification.target_value.replace(
                                  "city_id-",
                                  ""
                                )
                              )
                            : selectedNotification.target_value}
                    </Typography>
                  </Box>
                )}

                {selectedNotification.target_user_ids &&
                  selectedNotification.target_user_ids.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Target Users
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 0.5 }}
                      >
                        {selectedNotification.target_user_ids.map((userId) => (
                          <Chip
                            key={userId}
                            label={getUserDisplay(userId)}
                            size="small"
                            component={RouterLink}
                            to={DASHBOARD_USER.replace(":id", userId)}
                            clickable={true}
                            sx={{ textDecoration: "none" }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Sent By
                  </Typography>
                  <Typography variant="body2">
                    {selectedNotification.sent_by_user_email || "-"}
                    {selectedNotification.sent_by_user_id &&
                      ` (ID: ${selectedNotification.sent_by_user_id})`}
                  </Typography>
                </Box>

                {selectedNotification.fcm_message_id && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      FCM Message ID
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {selectedNotification.fcm_message_id}
                    </Typography>
                  </Box>
                )}

                {selectedNotification.delivery_errors &&
                  selectedNotification.delivery_errors.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Delivery Errors
                      </Typography>
                      <Alert severity="error" sx={{ mt: 0.5 }}>
                        {selectedNotification.delivery_errors.map((err) => (
                          <Typography
                            key={`${err.type}-${err.error}`}
                            variant="body2"
                          >
                            {err.type}: {err.error}
                          </Typography>
                        ))}
                      </Alert>
                    </Box>
                  )}

                {selectedNotification.data &&
                  Object.keys(selectedNotification.data).length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Custom Data
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{ p: 1, mt: 0.5, bgcolor: "grey.50" }}
                      >
                        <pre
                          style={{ margin: 0, fontSize: 12, overflow: "auto" }}
                        >
                          {JSON.stringify(selectedNotification.data, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Notification ID
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontSize: 12 }}
                  >
                    {selectedNotification.id}
                  </Typography>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              {selectedNotification.status === "scheduled" && (
                <Button
                  onClick={handleCancelNotification}
                  color="error"
                  disabled={cancelling}
                >
                  {cancelling ? "Cancelling..." : "Cancel Notification"}
                </Button>
              )}
              <Button onClick={() => setSelectedNotification(null)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};
